import React from "react"
import { ReportCharts, ChartConfig } from "../../../components/reports"
import reportData from "../data/attendee-report-2025-data.json"
import type { ReportData } from "./reportTypes"

// Cast the imported data to the proper type
const typedReportData: ReportData = reportData as ReportData

// Define color scheme for charts (using color scheme from the JSON data with fallbacks)
const colorScheme = typedReportData.chartColorScheme || {
  primary: '#7251f4', // Vibrant purple
  secondary: '#ff957d', // Vibrant orange/coral
  accent: '#d9629f', // Vibrant pink
  success: '#13cf94', // Vibrant green
  warning: '#ffb86c', // Softer peach/yellow that coordinates better
  danger: '#ff957d', // Updated orange color to match site branding
  info: '#2b4bc3', // Vibrant blue
  text: '#1F0F61', // Text color (dark blue)
  border: '#1F0F61' // Border color (dark blue)
}

interface AttendeeReportChartsProps {
  importanceChartRef: React.RefObject<HTMLCanvasElement>
  measurementGapChartRef: React.RefObject<HTMLCanvasElement>
  contentTypesChartRef: React.RefObject<HTMLCanvasElement>
  platformsChartRef: React.RefObject<HTMLCanvasElement>
  challengesSolutionsChartRef: React.RefObject<HTMLCanvasElement>
  technologyGapChartRef: React.RefObject<HTMLCanvasElement>
  futureImpactChartRef: React.RefObject<HTMLCanvasElement>
}

/**
 * Chart configuration for the Attendee Content Impact Report
 */
const AttendeeReportCharts: React.FC<AttendeeReportChartsProps> = ({
  importanceChartRef,
  measurementGapChartRef,
  contentTypesChartRef,
  platformsChartRef,
  challengesSolutionsChartRef,
  technologyGapChartRef,
  futureImpactChartRef,
}) => {

  // Define all chart configurations
  const charts: ChartConfig<any>[] = [
    // Importance Chart
    {
      type: 'doughnut',
      data: {
        labels: typedReportData.strategicImportance.importanceChart.labels,
        datasets: [{
          data: typedReportData.strategicImportance.importanceChart.data,
          backgroundColor: [colorScheme.primary, colorScheme.secondary, colorScheme.warning],
          borderWidth: 1,
          borderColor: '#fff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: { 
              padding: 10,
              font: { size: 9 }
            }
          }
        }
      },
      ref: importanceChartRef
    },
    
    // Measurement Gap Chart
    {
      type: 'bar',
      data: {
        labels: typedReportData.measurementGap.chartData.labels,
        datasets: [{
          label: 'Percentage of Respondents',
          data: typedReportData.measurementGap.chartData.data,
          backgroundColor: [colorScheme.primary, colorScheme.danger, colorScheme.warning],
          borderWidth: 1,
          borderColor: '#333'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            ticks: {
              callback: function(value) { return value + '%' },
              font: { size: 9 }
            }
          },
          x: {
            ticks: {
              font: { size: 8 }
            }
          }
        },
        plugins: {
          legend: { display: false }
        }
      },
      ref: measurementGapChartRef
    },
    
    // Content Types Chart
    {
      type: 'bar',
      data: {
        labels: typedReportData.contentTypes.chartData.typesLabels,
        datasets: [{
          data: typedReportData.contentTypes.chartData.typesData,
          backgroundColor: colorScheme.primary,
          borderWidth: 1,
          borderColor: '#333'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            ticks: {
              callback: function(value) { return value + '%' },
              font: { size: 9 }
            }
          },
          x: {
            ticks: {
              font: { size: 9 }
            }
          }
        },
        plugins: {
          legend: { display: false }
        }
      },
      ref: contentTypesChartRef
    },
    
    // Platforms Chart
    {
      type: 'bar',
      data: {
        labels: typedReportData.contentTypes.chartData.platformsLabels,
        datasets: [{
          data: typedReportData.contentTypes.chartData.platformsData,
          backgroundColor: colorScheme.secondary,
          borderWidth: 1,
          borderColor: '#333'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            max: 100,
            ticks: {
              callback: function(value) { return value + '%' },
              font: { size: 9 }
            }
          },
          x: {
            ticks: {
              font: { size: 9 }
            }
          }
        },
        plugins: {
          legend: { display: false }
        }
      },
      ref: platformsChartRef
    },
    
    // Challenges vs Solutions Chart
    {
      type: 'bar',
      data: {
        labels: typedReportData.challengesSolutions.chartData.labels,
        datasets: [
          {
            label: 'Current Challenges',
            data: typedReportData.challengesSolutions.chartData.challengesData,
            backgroundColor: colorScheme.danger,
            borderWidth: 1,
            borderColor: '#333'
          },
          {
            label: 'Desired Improvements',
            data: typedReportData.challengesSolutions.chartData.improvementsData,
            backgroundColor: colorScheme.success,
            borderWidth: 1,
            borderColor: '#333'
          }
        ]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        scales: {
          y: {
            beginAtZero: true,
            max: 70,
            ticks: {
              callback: function(value) { return value + '%' },
              font: { size: 9 }
            }
          },
          x: {
            ticks: {
              font: { size: 8 }
            }
          }
        },
        plugins: {
          legend: {
            labels: {
              font: { size: 9 }
            }
          }
        }
      },
      ref: challengesSolutionsChartRef
    },
    
    // Technology Gap Chart
    {
      type: 'doughnut',
      data: {
        labels: typedReportData.technologyGap.chartData.labels,
        datasets: [{
          data: typedReportData.technologyGap.chartData.data,
          backgroundColor: [colorScheme.danger, colorScheme.warning, colorScheme.info, colorScheme.success, colorScheme.primary],
          borderWidth: 1,
          borderColor: '#fff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: { 
              padding: 8,
              font: { size: 8 }
            }
          }
        }
      },
      ref: technologyGapChartRef
    },
    
    // Future Impact Chart
    {
      type: 'doughnut',
      data: {
        labels: typedReportData.futureImpact.chartData.labels,
        datasets: [{
          data: typedReportData.futureImpact.chartData.data,
          backgroundColor: [colorScheme.success, colorScheme.warning, colorScheme.danger],
          borderWidth: 1,
          borderColor: '#fff'
        }]
      },
      options: {
        responsive: true,
        maintainAspectRatio: false,
        plugins: {
          legend: {
            position: 'bottom',
            labels: { 
              padding: 10,
              font: { size: 9 }
            }
          }
        }
      },
      ref: futureImpactChartRef
    }
  ]

  return <ReportCharts charts={charts} />
}

export default AttendeeReportCharts